import { CommonModule } from '@angular/common'
import { Component, computed, inject, input } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'

import { delay, map } from 'rxjs'

import {
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalModule,
  SelectItem,
} from '@flowx/angular-ui-toolkit'
import { defaultJsonEditorOptions } from '@flowx/designer/modules/monaco-code-editor/constants'
import { CodeEditorComponent } from '@flowx/designer/modules/script-builder/components/code-editor/code-editor.component'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { JsonViewerComponent } from '@flowx/designer/shared/components/json-viewer/json-viewer.component'
import { DOCS_SLUG, DocsService } from '@flowx/designer/shared/services/docs.service'
import {
  ProcessDefinitionForInstance,
  ProcessInstance,
} from '@flowx/designer/shared/entities/process-instance.types'
import { selectQueryParams } from '@flowx/designer/shared/store/selectors/router.selectors'
import { downloadBlob, jsonToBlob } from '@flowx/shared/utils'

import { ProcessInstanceActiveStates } from '../../process.constants'
import { ProcessInstanceDetailsPanelHeaderComponent } from '../process-instance-details-panel-header/process-instance-details-panel-header.component'
import { ProcessInstanceVariablesEditorModalComponent } from '../process-instance-variables-editor-modal/process-instance-variables-editor-modal.component'

@Component({
  selector: 'app-process-instance-variables',
  imports: [
    CommonModule,
    JsonViewerComponent,
    ProcessInstanceVariablesEditorModalComponent,
    FlxModalModule,
    FlxFormControlModule,
    FlxButtonModule,
    ProcessInstanceDetailsPanelHeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    CodeEditorComponent,
  ],
  templateUrl: './process-instance-variables.component.html',
})
export class ProcessInstanceVariablesComponent {
  #ngrxStore = inject(Store)
  #router = inject(Router)
  #activatedRoute = inject(ActivatedRoute)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #docsService = inject(DocsService)

  processInstance = input.required<ProcessInstance>()
  processDefinition = input.required<ProcessDefinitionForInstance>()

  variableAccessRights = computed(() => {
    const permissions = this.#workspacesAndPermissionsStore.workspacePermissions.value()
    return {
      canEdit: !!permissions?.wks_process_instance_variables_edit,
    }
  })

  showEditorModal = this.#ngrxStore.select(selectQueryParams).pipe(
    map(({ showEditVariables }) => showEditVariables),
    // This delay is due to the positioning in the DOM of the modal relative to other overlays
    // like the process-instance overlay
    delay(0)
  )

  disableEditorModal = computed(() => {
    const processInstance = this.processInstance()
    if (!processInstance) {
      return true
    }
    return !ProcessInstanceActiveStates[processInstance.state]
  })

  viewVariablesOptions: SelectItem<string>[] = [
    { label: 'Tree View', value: 'tree' },
    { label: 'JSON view', value: 'json' },
  ]

  viewModeFC = new FormControl<SelectItem<string>>(this.viewVariablesOptions[0])
  viewModeValue = toSignal(this.viewModeFC.valueChanges.pipe(map(({ value }) => value)), {
    initialValue: this.viewVariablesOptions[0].value,
  })

  paramValues = computed(() => {
    const processInstance = this.processInstance()
    if (!Object.keys(processInstance?.paramValues || {}).length) {
      return null
    }
    return processInstance.paramValues
  })

  stringifiedVariables = computed(() => {
    const paramValues = this.paramValues()
    return JSON.stringify(paramValues || {}, null, 2)
  })
  defaultJsonEditorOptions = defaultJsonEditorOptions
  docsLink = this.#docsService.getDocsLink(DOCS_SLUG.UPDATE_PROCESS_VARIABLES)

  exportVariables(): void {
    const { paramValues, uuid } = this.processInstance()
    const { resourceName } = this.processDefinition()
    downloadBlob(jsonToBlob(paramValues || {}), `${resourceName}_${uuid}`)
  }

  toggleEditorModal(show = null): void {
    this.#router.navigate([], {
      relativeTo: this.#activatedRoute,
      queryParams: {
        showEditVariables: show,
      },
      queryParamsHandling: 'merge',
    })
  }
}
